import { COMMON_EMAIL_DOMAINS } from "@/constants";
import { UseFormReturn, FieldValues, FieldPath } from "react-hook-form";
import * as levenshtein from "fast-levenshtein";

// Common regex patterns for validation
export const VALIDATION_REGEX = {
  // Alphabets and spaces only
  ALPHABETS_SPACES: /^[a-zA-Z\s]+$/,

  // Alphabets, spaces, and common punctuation (.,&()-) for job titles and company names
  TEXT_WITH_PUNCTUATION: /^[a-zA-Z\s.,&()-]+$/,

  // Alphanumeric with spaces and common punctuation for company names
  ALPHANUMERIC_WITH_PUNCTUATION: /^[a-zA-Z0-9\s.,&()-]+$/,

  // Location format - alphabets, spaces, commas, periods, and hyphens
  LOCATION: /^[a-zA-Z\s,.-]+$/,

  // Phone number in E.164 format
  PHONE_E164: /^\+[1-9]\d{9,14}$/,

  // US postal code - exactly 5 digits
  POSTAL_CODE: /^\d{5}$/,
} as const;

type HasEmail = { email?: string };

export const validateEmailWatcher = async <TFieldValues extends FieldValues & HasEmail>(
  email: string | undefined,
  form: UseFormReturn<TFieldValues>,
) => {
  if (!email) return;

  const emailPath = "email" as FieldPath<TFieldValues>;

  const isFormEmailValid = await form.trigger(emailPath);
  if (!isFormEmailValid) {
    const { error } = form.getFieldState(emailPath);
    form.setError(emailPath, { message: error?.message || "Invalid email" });
  } else {
    form.clearErrors(emailPath);
  }
};

export const validateAndSuggestEmailDomain = (email: string) => {
  const [_localPart, domain] = email.split("@");

  if (!domain || domain.trim() === "") {
    return { isValid: false, suggestedDomain: null };
  }

  const normalizedDomain = domain.toLowerCase();

  // Calculate Levenshtein distance for each common domain
  const domainDistances = COMMON_EMAIL_DOMAINS.map((commonDomain) => ({
    domain: commonDomain.toLowerCase(),
    distance: levenshtein.get(normalizedDomain, commonDomain.toLowerCase()),
  }));

  // Sort domains by distance and select the closest match
  const closestDomain =
    domainDistances.sort((a, b) => a.distance - b.distance).find(({ distance }) => distance <= 3)
      ?.domain || null;

  const isValid = closestDomain === null || normalizedDomain === closestDomain;

  return {
    isValid: isValid,
    suggestedDomain: closestDomain || null,
  };
};
